import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Mail, Phone, Globe, MapPin, User, Calendar as CalIcon,
  Euro, Tag, Trash2, ArrowLeft, Send, Phone as PhoneIcon, Users, FileText,
  MessageSquare, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import BackButton from "../components/BackButton";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const ACT_TYPE = [
  { v: "call",             l: "Chiamata",       icon: PhoneIcon },
  { v: "meeting",          l: "Riunione",       icon: Users },
  { v: "email",            l: "Email",          icon: Mail },
  { v: "demo",             l: "Demo",           icon: FileText },
  { v: "follow_up",        l: "Follow-up",      icon: CalIcon },
  { v: "note",             l: "Nota",           icon: MessageSquare },
  { v: "proposal_sent",    l: "Proposta inviata", icon: Send },
  { v: "contract_signed",  l: "Contratto firmato", icon: CheckCircle2 },
];

function fmtEur(n) {
  if (n == null) return "—";
  try {
    return "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch { return `€ ${n}`; }
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; }
}
function fmtDt(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("it-IT"); } catch { return s; }
}

function ActivityDialog({ open, onOpenChange, leadId, onCreated }) {
  const [form, setForm] = useState({
    activity_type: "call", subject: "", description: "",
    completed_at: new Date().toISOString().slice(0, 16), outcome: "",
  });
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      const payload = {
        lead_id: leadId,
        activity_type: form.activity_type,
        subject: form.subject || null,
        description: form.description || null,
        outcome: form.outcome || null,
        completed_at: form.completed_at ? new Date(form.completed_at).toISOString() : null,
      };
      const created = await api.crmLogActivity(leadId, payload);
      toast.success("Attività registrata");
      onCreated?.(created);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="lead-activity-dialog">
        <DialogHeader><DialogTitle>Registra attività</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Tipo</Label>
            <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
              <SelectTrigger data-testid="act-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACT_TYPE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Oggetto</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                   data-testid="act-subject" />
          </div>
          <div>
            <Label>Data/ora</Label>
            <Input type="datetime-local" value={form.completed_at}
                   onChange={(e) => setForm({ ...form, completed_at: e.target.value })} />
          </div>
          <div>
            <Label>Descrizione</Label>
            <Textarea rows={2} value={form.description}
                       onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label>Esito</Label>
            <Input value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                   data-testid="act-outcome" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="act-submit">
            {saving ? "Salvataggio…" : "Registra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [stages, setStages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actOpen, setActOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [l, sts, acts] = await Promise.all([
        api.crmLead(leadId), api.crmStages(),
        api.crmActivities({ lead_id: leadId, limit: 50 }),
      ]);
      setLead(l); setStages(sts); setActivities(acts);
      setNotesDraft(l?.notes || "");
    } catch {
      toast.error("Errore caricamento lead");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [leadId]);

  async function moveStage(newStageId) {
    if (!lead || newStageId === lead.stage_id) return;
    try {
      const upd = await api.crmMoveStage(lead.id, { stage_id: newStageId });
      setLead(upd);
      toast.success("Stage aggiornato");
    } catch { toast.error("Errore"); }
  }

  async function saveNotes() {
    try {
      const upd = await api.crmLeadPatch(lead.id, { notes: notesDraft });
      setLead(upd);
      setEditingNotes(false);
      toast.success("Note salvate");
    } catch { toast.error("Errore"); }
  }

  async function deleteLead() {
    if (!window.confirm("Eliminare questo lead? L'azione è reversibile dal cestino DB.")) return;
    try {
      await api.crmLeadDelete(lead.id);
      toast.success("Lead eliminato");
      nav("/prime-vision/crm");
    } catch { toast.error("Errore eliminazione"); }
  }

  const currentStage = stages.find((s) => s.id === lead?.stage_id);

  if (loading || !lead) {
    return <div className="pxos-card p-8 text-center opacity-70" data-testid="lead-loading">Caricamento…</div>;
  }

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Bug 21/50 · Shell.auto già mostra BackButton su questo path (non root) */}

      <div className="pxos-card p-6" data-testid="lead-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold">{lead.contact_name}</h1>
            {lead.business_name && (
              <div className="mt-1 flex items-center gap-2 opacity-80">
                <Building2 size={16} /> {lead.business_name}
                {lead.role && <span className="opacity-70">· {lead.role}</span>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {currentStage && (
              <Badge variant="outline" data-testid="lead-current-stage"
                     style={{ borderColor: currentStage.color || "#B8B8B8", color: "var(--pxos-text-dark)" }}>
                <span className="w-2 h-2 rounded-full mr-1.5" style={{ background: currentStage.color || "#B8B8B8" }} />
                {currentStage.name}
              </Badge>
            )}
            <Select value={lead.stage_id} onValueChange={moveStage}>
              <SelectTrigger className="w-56" data-testid="lead-stage-select">
                <SelectValue placeholder="Cambia stage…" />
              </SelectTrigger>
              <SelectContent>
                {stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="destructive" size="sm" onClick={deleteLead} data-testid="lead-delete">
              <Trash2 size={14} className="mr-1" /> Elimina
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5 text-sm">
          <div>
            <div className="opacity-60 text-xs uppercase tracking-widest">Valore potenziale</div>
            <div className="font-semibold">{fmtEur(lead.potential_value)}</div>
          </div>
          <div>
            <div className="opacity-60 text-xs uppercase tracking-widest">Probabilità</div>
            <div className="font-semibold">{lead.probability_percent != null ? `${lead.probability_percent}%` : "—"}</div>
          </div>
          <div>
            <div className="opacity-60 text-xs uppercase tracking-widest">Data chiusura attesa</div>
            <div className="font-semibold">{fmtDate(lead.expected_close_date)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Colonna sinistra: dati contatto + note */}
        <div className="md:col-span-1 space-y-4">
          <div className="pxos-card p-5" data-testid="lead-contact-box">
            <h2 className="font-display text-lg font-semibold mb-3">Contatti</h2>
            <div className="space-y-2 text-sm">
              {lead.email && <div className="flex items-center gap-2"><Mail size={14} className="opacity-60" /> <a href={`mailto:${lead.email}`} className="underline break-all">{lead.email}</a></div>}
              {lead.phone && <div className="flex items-center gap-2"><Phone size={14} className="opacity-60" /> <a href={`tel:${lead.phone}`} className="underline">{lead.phone}</a></div>}
              {lead.website && <div className="flex items-center gap-2"><Globe size={14} className="opacity-60" /> <a href={lead.website} target="_blank" rel="noreferrer" className="underline break-all">{lead.website}</a></div>}
              {lead.city && <div className="flex items-center gap-2"><MapPin size={14} className="opacity-60" /> {lead.city}</div>}
              {lead.source && <div className="flex items-center gap-2"><Tag size={14} className="opacity-60" /> Fonte: {lead.source}</div>}
              {lead.responsible && <div className="flex items-center gap-2"><User size={14} className="opacity-60" /> Responsabile: {lead.responsible}</div>}
            </div>
          </div>

          <div className="pxos-card p-5" data-testid="lead-next-action-box">
            <h2 className="font-display text-lg font-semibold mb-2">Prossima azione</h2>
            <div className="text-sm opacity-80">{lead.next_action || "—"}</div>
            <div className="text-xs opacity-60 mt-1">{fmtDate(lead.next_action_date)}</div>
          </div>

          <div className="pxos-card p-5" data-testid="lead-notes-box">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-lg font-semibold">Note</h2>
              {!editingNotes && (
                <Button size="sm" variant="ghost" onClick={() => setEditingNotes(true)}>Modifica</Button>
              )}
            </div>
            {editingNotes ? (
              <div className="space-y-2">
                <Textarea rows={4} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => { setNotesDraft(lead.notes || ""); setEditingNotes(false); }}>Annulla</Button>
                  <Button size="sm" onClick={saveNotes}>Salva</Button>
                </div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-sm opacity-80 min-h-[3em]">
                {lead.notes || <span className="opacity-50 italic">Nessuna nota.</span>}
              </div>
            )}
          </div>
        </div>

        {/* Colonna destra: attività timeline */}
        <div className="md:col-span-2">
          <div className="pxos-card p-5" data-testid="lead-activities-box">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-semibold">Attività</h2>
              <Button size="sm" onClick={() => setActOpen(true)} data-testid="lead-activity-add">
                <MessageSquare size={14} className="mr-1" /> Registra attività
              </Button>
            </div>
            {activities.length === 0 ? (
              <div className="text-sm opacity-70 text-center py-6" data-testid="lead-activities-empty">
                Nessuna attività registrata.
              </div>
            ) : (
              <ul className="space-y-3" data-testid="lead-activities-list">
                {activities.map((a) => {
                  const typ = ACT_TYPE.find((x) => x.v === a.activity_type);
                  const Icon = typ?.icon || MessageSquare;
                  return (
                    <li key={a.id} className="flex gap-3 pb-3 border-b" style={{ borderColor: "var(--pxos-border)" }}>
                      <div className="shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <Icon size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                          <span className="font-medium">{typ?.l || a.activity_type}</span>
                          {a.subject && <span className="opacity-80">· {a.subject}</span>}
                        </div>
                        {a.description && <div className="text-sm opacity-70 mt-0.5 whitespace-pre-wrap">{a.description}</div>}
                        {a.outcome && <div className="text-xs mt-1 opacity-80">Esito: <em>{a.outcome}</em></div>}
                        <div className="text-xs opacity-50 mt-1">
                          {fmtDt(a.completed_at || a.scheduled_at || a.created_at)}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <ActivityDialog open={actOpen} onOpenChange={setActOpen}
                       leadId={lead.id}
                       onCreated={(a) => { setActivities((prev) => [a, ...prev]); load(); }} />
    </div>
  );
}
